import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ReactNode } from 'react';
import CookieSettings from './CookieSettings';

interface CookieConsentLink {
  link: string;
  title: ReactNode;
}

interface CookieConsentSetting {
  checked: boolean;
  cookieName: string;
  editable: boolean;
  info: ReactNode;
  title: ReactNode;
}

interface CookieConsentComponentProps {
  acceptButtonLabel: ReactNode;
  fullHeightSettings?: boolean;
  info?: ReactNode;
  links?: CookieConsentLink[];
  maxSettingsHeight?: string;
  onAccept: () => void;
  onSettingsChange?: (event: React.MouseEvent) => void;
  onSettingsClick?: () => void;
  onSettingsSave?: () => void;
  saveSettingsButtonLabel?: ReactNode;
  settings?: CookieConsentSetting[];
  settingsButtonLabel?: ReactNode;
  settingsOpen?: boolean;
}

const styles = {
  content: {
    height: '100%',
  },
  fullHeight: {
    borderRadius: 0,
    bottom: 0,
    height: '100%',
    left: 'auto',
    right: 0,
    width: '316px',
  },
  panel: {
    bottom: 2,
    left: 'auto',
    right: 2,
  },
  root: {
    backgroundColor: 'grey.300',
    borderRadius: 1,
    padding: 2,
    position: 'fixed',
    width: 300,
    zIndex: 10000,
  },
};

function CookieConsentComponent({
  acceptButtonLabel,
  fullHeightSettings = false,
  info = '',
  links = [],
  maxSettingsHeight = 'none',
  onAccept,
  onSettingsChange = () => {},
  onSettingsClick = () => {},
  onSettingsSave = () => {},
  saveSettingsButtonLabel = '',
  settings = [],
  settingsButtonLabel = '',
  settingsOpen = false,
}: CookieConsentComponentProps) {
  return (
    <Box
      component="div"
      sx={{
        ...styles.root,
        ...((!fullHeightSettings || !settingsOpen) && styles.panel),
        ...(fullHeightSettings && settingsOpen && styles.fullHeight),
      }}
    >
      <Grid container spacing={2} sx={{ ...styles.content, flexDirection: 'column', justifyContent: 'space-between' }}>
        <Grid container sx={{ flexDirection: 'column' }}>
          <Box sx={{ pb: 3 }}>
            {info && (
              <Grid>
                <Typography variant="caption">{info}</Typography>
              </Grid>
            )}
            {links.length > 0 &&
              links.map((item) => (
                <Grid key={item.link}>
                  <Box sx={{ pt: 1 }}>
                    <Link href={item.link} rel="noopener" target="_blank">
                      <Typography variant="caption">{item.title}</Typography>
                    </Link>
                  </Box>
                </Grid>
              ))}
          </Box>
          {settingsOpen && (
            <Grid>
              <Box sx={{ pb: 2 }}>
                <CookieSettings maxHeight={maxSettingsHeight} onChange={onSettingsChange} settings={settings} />
              </Box>
              {saveSettingsButtonLabel && (
                <Button color="primary" onClick={onSettingsSave} variant="contained">
                  {saveSettingsButtonLabel}
                </Button>
              )}
            </Grid>
          )}
        </Grid>
        <Grid container direction="row" spacing={2}>
          <Grid>
            <Button color="primary" data-testid="accept-cookies-button" onClick={onAccept} variant="contained">
              {acceptButtonLabel}
            </Button>
          </Grid>
          {settings.length > 0 && (
            <Grid>
              <Button color="primary" onClick={onSettingsClick}>
                {settingsButtonLabel}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default CookieConsentComponent;
